import { useCalculationTypes } from "@/dataProviders/CalculationTypes";
import { DenominatorUnitType, IndicatorTypeType } from "@/dataProviders/Indicators";
import { InsertProjectTransportMappingInput } from "@/features/TransportMappingList/providers/TransportMappingListProvider";
import {
  GetTransportSourcesProjectInstallationQuery,
  Project_Transport_Mapping_Insert_Input,
  useGetProjectTransportMappingLazyQuery,
  useGetTransportSourcesProjectInstallationLazyQuery,
  useInsertProjectTransportMappingsMutation,
} from "@/graphql";
import { useDashboard } from "@/stores/dashboard/dashboard.store";
import { defineStore } from "pinia";
import { v4 as uuid } from "uuid";
import { computed, ref, watch } from "vue";
import withApolloClient from "@/graphql/services/withApolloClient";
import { editedSince } from "@/features/BuildingProject/services/editedSince";

export type MachineryFuelEmissionFactorItem = {
  id: string;
  dataSourceName: string;
  emissionFactorLabel: string;
  inputs: { value: number; unit: string }[];
  totalEmission: { value: number; unit: string };
  mapping: InsertProjectTransportMappingInput;
};

const useInstallationEmissionFactors = defineStore("InstallationEmissionFactorsProvider", () => {
  const items = ref<MachineryFuelEmissionFactorItem[]>([]);

  /**
   * The rows as last read from the server, serialised the way `save` sends them.
   *
   * Null until the first read, which `hasUnsavedChanges` treats as "unknown, so send it" — the same
   * rule the project snapshot uses in the edit flow. Skipping a write we should have made costs the
   * user their edit; making one we could have skipped costs a round trip.
   */
  const savedSignature = ref<string | null>(null);
  const calculationTypeIndicator = ref<{
    numerator: IndicatorTypeType;
    denominator: DenominatorUnitType;
  }>();
  const calculationTypes = useCalculationTypes();
  // Mark the dashboard/BCI stale after installation transport mutations so the transport &
  // installation budget updates without a page reload (RTLCA-4865, RTLCA-5738)
  const { invalidateDashboard } = useDashboard();

  const transportSourcesProjectInstallationQuery = withApolloClient(() => useGetTransportSourcesProjectInstallationLazyQuery());

  const {
    mutate: insertProjectTransportMappingsMutation,
    loading: insertProjectTransportMappingsLoading,
    error: insertProjectTransportMappingsError,
  } = withApolloClient(() => useInsertProjectTransportMappingsMutation());

  // Get the project transport mapping
  const {
    load: loadTransportMappings,
    result: transportMappings,
    loading: transportMappingsLoading,
    error: transportMappingsError,
  } = withApolloClient(() => useGetProjectTransportMappingLazyQuery());

  const error = computed(() => {
    return insertProjectTransportMappingsError.value || transportMappingsError.value;
  });

  const loading = computed(() => {
    return (
      insertProjectTransportMappingsLoading.value ||
      transportSourcesProjectInstallationQuery.loading.value ||
      transportMappingsLoading.value
    );
  });

  const transportSources = computed(() => {
    return transportSourcesProjectInstallationQuery.result.value?.transportSource || [];
  });

  const mapPayloadToItem = (payload: InsertProjectTransportMappingInput) => {
    const totalEmission = {
      value: payload.transportEmissionFactorEmission || 0,
      unit: calculationTypeIndicator.value?.numerator.unit || "",
    };

    const target = payload.emissionFactors?.[0];

    let dataSource: GetTransportSourcesProjectInstallationQuery["transportSource"][0] | undefined = undefined;
    let emissionFactorLabel;
    if (target) {
      for (const source of transportSources.value) {
        const match = source.transportEmissionFactors.find((x) => x.id === target.transportEmissionFactorId);

        if (match) {
          dataSource = source;
          emissionFactorLabel = match.label;
          break;
        }
      }
    }

    const dataSourceName = dataSource?.label;

    const inputs: { unit: string; value: number }[] = [];

    payload.emissionFactors?.forEach((x) => {
      const emissionFactor = dataSource?.transportEmissionFactors.find((ef) => ef.id === x.transportEmissionFactorId);
      if (!emissionFactor) return;

      inputs.push({
        unit: emissionFactor.denominatorUnit.unit || "",
        value: totalEmission.value / parseFloat(emissionFactor.emission),
      });
    });

    if (dataSourceName && emissionFactorLabel) {
      return {
        id: uuid(),
        dataSourceName,
        emissionFactorLabel,
        totalEmission,
        inputs,
        mapping: payload,
      };
    }

    return undefined;
  };

  watch([transportMappings, transportSources], ([newTransportMappings, newTransportSources]) => {
    const result: MachineryFuelEmissionFactorItem[] = [];

    if (newTransportSources.length > 0) {
      newTransportMappings?.project_transport_mapping.forEach((item) => {
        const mappedItem = mapPayloadToItem({
          ...item,
          emissionFactors: item.emissionFactors.map((ef) => ({
            ...ef,
            emissionFactor: {
              data: {
                ...ef.emissionFactor,
                denominatorUnit: undefined,
                transportSource: undefined,
              },
            },
            transportSource: undefined,
          })),
        });

        if (mappedItem) {
          result.push(mappedItem);
        }
      });
    }

    items.value = result;
    // What the server holds, in the shape `save` would send it back in. Taken here rather than in
    // `save` so it survives the whole editing session and can say whether anything actually moved.
    savedSignature.value = signatureOf(result);
  });

  const add = (payload: InsertProjectTransportMappingInput) => {
    const totalEmission = {
      value: payload.transportEmissionFactorEmission || 0,
      unit: calculationTypeIndicator.value?.numerator.unit || "",
    };

    const target = payload.emissionFactors?.[0];

    let dataSource: GetTransportSourcesProjectInstallationQuery["transportSource"][0] | undefined = undefined;
    let emissionFactorLabel;
    if (target) {
      for (const source of transportSources.value) {
        const match = source.transportEmissionFactors.find((x) => x.id === target.transportEmissionFactorId);
        if (match) {
          dataSource = source;
          emissionFactorLabel = match.label;
          break;
        }
      }
    }

    const dataSourceName = dataSource?.label;

    const inputs: { unit: string; value: number }[] = [];

    payload.emissionFactors?.forEach((x) => {
      const emissionFactor = dataSource?.transportEmissionFactors.find((ef) => ef.id === x.transportEmissionFactorId);
      if (!emissionFactor) return;

      inputs.push({
        unit: emissionFactor.denominatorUnit.unit || "",
        value: totalEmission.value / parseFloat(emissionFactor.emission),
      });
    });

    if (dataSourceName && emissionFactorLabel) {
      items.value.push({
        id: uuid(),
        dataSourceName,
        emissionFactorLabel,
        totalEmission,
        inputs,
        mapping: payload,
      });
    }
  };

  const fetchItems = async (projectId: string) => {
    await loadTransportMappings(null, {
      project_id: projectId,
      materialIdIsNull: true,
    });
  };

  const fetchSources = async (calculationTypeId: number) => {
    const calculationType = calculationTypes.getCalculationTypes().find((item) => item.id === calculationTypeId);
    calculationTypeIndicator.value = {
      numerator: calculationType?.thresholdIndicator.indicatorType as IndicatorTypeType,
      denominator: calculationType?.thresholdIndicator.denominatorUnit as DenominatorUnitType,
    };

    await transportSourcesProjectInstallationQuery.load(null, {
      calculationTypeId,
    });
  };

  const remove = (id: string) => {
    items.value = items.value.filter((item) => item.id !== id);
  };

  /**
   * The part of an item the save actually sends: its emission total and its emission-factor ids.
   * Deliberately not the whole item — ids and labels come back from the server and would make an
   * untouched list look changed on every read.
   */
  const signatureOf = (rows: MachineryFuelEmissionFactorItem[]): string =>
    JSON.stringify(
      rows.map((item) => ({
        emission: item.totalEmission.value,
        factors: item.mapping.emissionFactors?.map((factor) => factor.transportEmissionFactorId) ?? [],
      }))
    );

  /**
   * Whether saving would send anything the server does not already have.
   *
   * The edit flow used to call `save` on every save of any field, so changing a project's status
   * rewrote these mappings unchanged — a wasted round trip, a dashboard refresh behind it, and a
   * `project_version` row that put a mapping change in the project's history that nobody made.
   */
  const hasUnsavedChanges = (): boolean => savedSignature.value === null || signatureOf(items.value) !== savedSignature.value;

  /**
   * Whether the reader has edited these rows — the same comparison as above with the unknown case
   * answered the other way, which is why it is a second function and not a call to the first.
   *
   * The settings page's rail marks the sections you have touched, and Installation (A5) is one of the
   * three whose state does not all live on the project: these rows are held here, so editing only
   * them used to leave the section looking untouched. A write that cannot tell must send; a marker
   * that cannot tell must stay dark. See `editedSince` for that rule in full.
   */
  const hasEdits = (): boolean => editedSince(signatureOf(items.value), savedSignature.value);

  const save = async (projectId: string) => {
    withApolloClient(async () => {
      const objects: Project_Transport_Mapping_Insert_Input[] = items.value.map((item) => ({
        distance: 0,
        project_id: projectId,
        transportEmissionFactorEmission: item.totalEmission.value,
        emissionFactors: {
          data:
            item.mapping.emissionFactors?.map((ef) => ({
              transportEmissionFactorId: ef.transportEmissionFactorId,
            })) || [],
        },
      }));

      await insertProjectTransportMappingsMutation({
        objects,
        materialIdIsNull: true,
        projectId,
      });

      // The server now holds what we just sent, so the next save has nothing to send unless the
      // reader edits again.
      savedSignature.value = signatureOf(items.value);

      // Reflect the change on the dashboard/BCI without a page reload (RTLCA-4865) — as a MARK on
      // the project just written rather than a fetch (RTLCA-5738). Both callers of this save are
      // pages with no dashboard on screen, and neither could be served by a fetch from here: on the
      // settings page it read the store's own `projectId`, which is the project the reader last had
      // a dashboard for and not necessarily this one, and during project creation there is no
      // dashboard to refresh yet (see `refreshDashboard`'s own guard). The settings save marks the
      // store as well, so a save that also moved the calculation is one mark and one fetch, not two
      // fetches.
      invalidateDashboard(projectId);
    });
  };

  return {
    add,
    error,
    fetchItems,
    fetchSources,
    hasEdits,
    hasUnsavedChanges,
    items,
    loading,
    remove,
    save,
    transportSources,
  };
});

export default useInstallationEmissionFactors;
